import type { ReactElement } from 'react';
import { useTranslation } from '@govard/i18n-core/react';
import { CalendarDays } from 'lucide-react';
import { List, Typography } from 'antd';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { FocusPriority } from './types.js';

const { Text } = Typography;

export interface CalendarDeadline {
  id: string;
  title: string;
  date: string;
  project?: string;
  priority?: FocusPriority;
}

export interface CalendarWidgetProps {
  deadlines?: readonly CalendarDeadline[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onItemClick?: (deadline: CalendarDeadline) => void;
}

function getDaysUntil(date: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const diff = Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000);
  if (diff < 0) {
    return t('crm.dashboard.calendar.overdue');
  }
  if (diff === 0) {
    return t('crm.dashboard.calendar.today');
  }
  if (diff === 1) {
    return t('crm.dashboard.calendar.tomorrow');
  }
  return t('crm.dashboard.calendar.in_days', { count: diff });
}

function getColorForDate(date: string): string {
  const diff = Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000);
  if (diff < 0) {
    return '#dc2626'; // error red
  }
  if (diff <= 1) {
    return '#d97706'; // warning amber
  }
  if (diff <= 3) {
    return '#2563eb'; // info blue
  }
  return '#a8a29e'; // muted stone
}

export function CalendarWidget({ deadlines = [], className, testId, loading = false, onItemClick }: CalendarWidgetProps): ReactElement {
  const { t } = useTranslation();
  
  const widgetContainerProps = {
    title: t('crm.dashboard.calendar.title'),
    icon: CalendarDays,
    iconColor: '#2563eb',
    minHeight: 180,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div className="animate-pulse p-4 flex flex-col gap-2">
          {[1, 2, 3].map((index) => (
            <div key={index} className="rounded h-8 bg-stone-200/50 w-full" />
          ))}
        </div>
      ) : deadlines.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-6 opacity-50 h-[140px]">
          <CalendarDays size={24} className="mb-2 text-stone-400" />
          <Text type="secondary" style={{ fontSize: 11 }}>{t('crm.dashboard.calendar.empty')}</Text>
        </div>
      ) : (
        <List
          size="small"
          dataSource={deadlines as CalendarDeadline[]}
          renderItem={(deadline) => {
            const accentColor = getColorForDate(deadline.date);

            return (
              <List.Item style={{ padding: 0 }}>
                <button
                  type="button"
                  onClick={onItemClick ? () => onItemClick(deadline) : undefined}
                  className="transition-colors text-left border-0 flex w-full items-center gap-3"
                  style={{
                    cursor: onItemClick ? 'pointer' : 'default',
                    opacity: onItemClick ? 1 : 0.8,
                    padding: '8px 16px',
                    borderBottom: '1px solid #f5f5f4',
                    background: 'transparent',
                  }}
                >
                  <div style={{ width: 4, height: 24, borderRadius: 2, backgroundColor: accentColor, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <Text strong className="truncate block" style={{ fontSize: 13, color: '#44403c', lineHeight: 1.2 }}>
                      {deadline.title}
                    </Text>
                    {deadline.project ? (
                      <Text type="secondary" style={{ fontSize: 10, display: 'block', marginTop: 2 }}>{deadline.project}</Text>
                    ) : null}
                  </div>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: accentColor, fontWeight: 600 }}>
                    {getDaysUntil(deadline.date, t)}
                  </span>
                </button>
              </List.Item>
            );
          }}
        />
      )}
    </WidgetContainer>
  );
}
